import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const cardVariants = cva(
  "rounded-2xl text-card-foreground transition-all",
  {
    variants: {
      variant: {
        // Material Filled Card - White background
        default:
          "bg-white dark:bg-slate-900 shadow-md hover:shadow-lg",
        // Material Elevated Card
        elevated:
          "bg-white dark:bg-slate-900 shadow-lg hover:shadow-xl hover:-translate-y-0.5",
        // Material Outlined Card
        outlined:
          "bg-white dark:bg-slate-900 border border-border hover:border-primary/30",
        // Flat/Ghost Card
        ghost:
          "bg-transparent border-0 shadow-none",
        // Surface Container Card
        surface:
          "bg-slate-50 dark:bg-slate-800/50 border-0 shadow-none",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant }), className)}
      {...props}
    />
  )
)
Card.displayName = "Card"

export interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  icon?: React.ReactNode
  iconColor?: 'primary' | 'blue' | 'green' | 'red' | 'orange' | 'purple' | 'teal' | 'cyan' | 'pink'
  actions?: React.ReactNode
}

const iconColorClasses = {
  primary: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  blue: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400',
  green: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400',
  red: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400',
  orange: 'bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400',
  purple: 'bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400',
  teal: 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400',
  cyan: 'bg-cyan-100 text-cyan-600 dark:bg-cyan-900/30 dark:text-cyan-400',
  pink: 'bg-pink-100 text-pink-600 dark:bg-pink-900/30 dark:text-pink-400',
}

const CardHeader = React.forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, icon, iconColor = 'primary', actions, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex items-center gap-4 p-5 border-b border-slate-100 dark:border-slate-800",
        className
      )}
      {...props}
    >
      {icon && (
        <div className={cn(
          "flex items-center justify-center w-10 h-10 rounded-full shrink-0",
          iconColorClasses[iconColor]
        )}>
          {React.isValidElement(icon) && React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
            className: cn("h-5 w-5", (icon as React.ReactElement<{ className?: string }>).props.className)
          })}
        </div>
      )}
      <div className="flex-1 min-w-0">
        {children}
      </div>
      {actions && (
        <div className="shrink-0">
          {actions}
        </div>
      )}
    </div>
  )
)
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "card-title text-base font-semibold leading-tight tracking-tight text-gray-800 dark:text-gray-100",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-sm text-muted-foreground leading-relaxed", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-5", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-6 pt-0", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent, cardVariants }
